import { navigate } from "../router";
import { store, type ProfanityLevel } from "../state";
import { openLookSheet } from "../look";
import { PROFANITY_STEPS, getReachedHeadline, getReachedPunchline } from "../copy";
import { censorText, getDailyCopy, getDailyHeadline, localDayKey } from "../copy/engine";
import { shareCountdownCard } from "../share";
import { GEAR_SVG, SHARE_SVG, SUB, countWorkdays, escapeHtml, fmt, formatEventDate } from "./countdownSticker";
import { dialCellsHTML, mountDial } from "../ui/dial";
import { createFitter, cssPx } from "../ui/fit";
import { countUp, playEntrance, setCount, takeFirstRun } from "../ui/entrance";
import { createTimeLeft, everySecond, pulse } from "./clock";
import { bellHTML, mountBell } from "./reminderBell";

/**
 * "Neumorphic" countdown: the glossy chrome direction from the chrome-plus
 * preview in public/previews as a live screen, in its purple colourway
 * (chrome.css).
 */

const HERO_MAX_PX = 90;

export function renderCountdownChrome(root: HTMLElement): () => void {
  const event = store.getPrimaryEvent();
  if (!event) {
    navigate("#/edit", { kind: "none" });
    return () => {};
  }

  root.innerHTML = `
    <div class="chr" data-el="screen">
      <div class="chr-sheen"></div>
      ${["a", "b", "c", "d", "e", "f"].map((k) => `<div class="chr-blob chr-blob-${k}"></div>`).join("")}
      <div class="chr-grain"></div>

      <div class="chr-content">
        <div class="chr-topbar">
          <div class="chr-wordmark"><span class="chr-orb"></span><span>C<span class="chr-censor">*</span>UNTDOWN</span></div>
          <div class="chr-icons">
            <button type="button" class="chr-icon-btn" data-el="shareBtn" aria-label="Share this countdown">${SHARE_SVG}</button>
            <button type="button" class="chr-icon-btn" data-el="lookBtn" aria-label="Look and settings" aria-haspopup="dialog">${GEAR_SVG}</button>
          </div>
        </div>

        <div class="chr-ticket chr-glass">
          <span class="chr-ticket-name">${escapeHtml(event.name || "The reckoning")}</span>
          <span class="cd-ticket-end">${bellHTML("chr-bell")}<span class="chr-ticket-date">${formatEventDate(event.targetDate)}</span></span>
        </div>

        <div class="chr-headline" data-el="headline"></div>
        <div class="chr-sub">${SUB}</div>

        <div class="chr-units">
          <div class="chr-hero" data-el="hero">
            <div class="chr-hero-shine"></div>
            <span class="chr-hero-num" data-el="numDays"></span>
            <span class="chr-hero-tag" data-el="daysTag">days ♡</span>
          </div>
          <div class="chr-chip chr-glass"><span class="chr-chip-num" data-el="numWorkdays"></span><span class="chr-chip-label">work days</span></div>
          <div class="chr-chip chr-glass"><span class="chr-chip-num" data-el="numHours"></span><span class="chr-chip-label">hours</span></div>
          <div class="chr-seconds"><span class="chr-seconds-lead">for reference, </span>that's exactly <b data-el="numSeconds"></b> seconds</div>
        </div>

        <div class="chr-punchline chr-glass" data-el="punchline">
          <div class="chr-punchline-main" data-el="punchlineMain"></div>
          <div class="chr-punchline-sub" data-el="punchlineSub"></div>
        </div>

        <div class="chr-bottom">
          <div>
            <div class="chr-caption-row"><span data-el="pctLabel"></span><span class="chr-accent" data-el="leftLabel"></span></div>
            <div class="chr-track"><div class="chr-track-fill" data-el="trackFill"></div></div>
          </div>
          <div class="chr-dial-row cd-dial" data-el="dialRow">${dialCellsHTML("chr-bubble")}</div>
        </div>
      </div>
    </div>
  `;

  const el = <T extends HTMLElement = HTMLElement>(name: string) => root.querySelector<T>(`[data-el="${name}"]`)!;
  const screen = el("screen");
  const content = root.querySelector<HTMLElement>(".chr-content")!;
  const headline = el("headline");
  const hero = el("hero");
  const numDays = el("numDays");
  const daysTag = el("daysTag");
  const numWorkdays = el("numWorkdays");
  const numHours = el("numHours");
  const numSeconds = el("numSeconds");
  const punchline = el("punchline");
  const punchlineMain = el("punchlineMain");
  const punchlineSub = el("punchlineSub");
  const pctLabel = el("pctLabel");
  const leftLabel = el("leftLabel");
  const trackFill = el("trackFill");
  const shareBtn = el<HTMLButtonElement>("shareBtn");
  const lookBtn = el<HTMLButtonElement>("lookBtn");

  lookBtn.addEventListener("click", () => openLookSheet(lookBtn));

  // The dial is a glossy pill that jellies across to the picked level. Each
  // change fizzes soap bubbles up out of it (more the ruder it gets), brings
  // the punchline card back into focus, and on F**ked the hero wobbles.
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const thumb = root.querySelector<HTMLElement>(".dial-thumb")!;

  function fizz(from: HTMLElement, count: number, level: ProfanityLevel) {
    const box = screen.getBoundingClientRect();
    const c = from.getBoundingClientRect();
    for (let i = 0; i < count; i++) {
      const b = document.createElement("span");
      b.className = "chr-fizz";
      const size = 10 + Math.random() * (12 + level * 8);
      b.style.width = b.style.height = `${size}px`;
      b.style.left = `${c.left - box.left + c.width * (0.15 + Math.random() * 0.7) - size / 2}px`;
      b.style.top = `${c.top - box.top + c.height * 0.3}px`;
      screen.appendChild(b);
      const rise = 160 + Math.random() * (140 + level * 110);
      const sway = (Math.random() - 0.5) * 70;
      const anim = b.animate(
        [
          { transform: "translate(0, 0) scale(.2)", opacity: 0 },
          { transform: `translate(${sway * 0.4}px, -${rise * 0.3}px) scale(1)`, opacity: 1, offset: 0.25 },
          { transform: `translate(${-sway * 0.3}px, -${rise * 0.7}px) scale(1)`, opacity: 0.9, offset: 0.7 },
          { transform: `translate(${sway}px, -${rise}px) scale(1.35)`, opacity: 0 },
        ],
        { duration: 900 + Math.random() * 700 - level * 150, delay: i * (50 - level * 12), easing: "cubic-bezier(.3,.6,.4,1)", fill: "backwards" }
      );
      anim.onfinish = () => b.remove();
    }
  }

  const dial = mountDial(el("dialRow"), (level, _prev, cell) => {
    if (reducedMotion) return;
    thumb.animate(
      [{ scale: "1 1" }, { scale: "1.22 .82", offset: 0.3 }, { scale: ".94 1.08", offset: 0.62 }, { scale: "1 1" }],
      { duration: 560, easing: "ease-out" }
    );
    for (const part of [punchlineMain, punchlineSub, headline]) {
      part.animate([{ filter: "blur(7px)", opacity: 0.2 }, { filter: "blur(0)", opacity: 1 }], { duration: 420, easing: "ease-out" });
    }
    fizz(cell, [4, 8, 16][level], level);
    if (level === 2) {
      hero.animate(
        [{ scale: "1 1" }, { scale: "1.05 .95", offset: 0.2 }, { scale: ".97 1.03", offset: 0.45 }, { scale: "1.015 .985", offset: 0.7 }, { scale: "1 1" }],
        { duration: 620, easing: "ease-out" }
      );
    }
  });

  let wasReached = false;
  const copyContext = (level: ProfanityLevel) => ({
    level,
    eventId: event.id,
    eventName: event.name,
    personName: event.personName,
    target: event.targetDate,
  });

  function applyCopy(level: ProfanityLevel) {
    punchline.dataset.level = PROFANITY_STEPS[level].label;
    screen.dataset.level = String(level);
    dial.sync(level);
    const censorOn = store.get().censorEnabled;
    if (wasReached) {
      headline.textContent = getReachedHeadline(event!.personName, level);
      punchlineMain.textContent = censorText(getReachedPunchline(event!.name, level), level, censorOn);
      punchlineSub.textContent = "";
    } else {
      headline.textContent = getDailyHeadline(copyContext(level));
      const copy = getDailyCopy(copyContext(level));
      // Lower-case "of": it reads on from the numbers above.
      punchlineMain.textContent = `of ${censorText(copy.main, level, censorOn)}`;
      punchlineSub.textContent = censorText(copy.sub, level, censorOn);
    }
  }

  // Shrink the hero numeral only when it would overflow its card.
  let lastDaysLen = -1;
  function fitHero() {
    const max = cssPx(screen, "--hero-max", HERO_MAX_PX);
    numDays.style.fontSize = `${max}px`;
    const avail = hero.clientWidth - 36;
    const w = numDays.scrollWidth;
    if (w > avail) numDays.style.fontSize = `${Math.max(40, Math.floor((max * avail) / w))}px`;
  }
  const fitter = createFitter(content, screen, fitHero);

  let lastDayKey = localDayKey(new Date());
  const timeLeft = createTimeLeft(leftLabel, fmt, reducedMotion);
  function tick() {
    const now = Date.now();
    const target = new Date(event!.targetDate).getTime();
    const start = new Date(event!.startDate).getTime();
    const remaining = Math.max(0, target - now);
    const reached = remaining <= 0;
    const days = Math.floor(remaining / 86400000);

    const daysText = fmt(days);
    setCount(numDays, daysText);
    daysTag.textContent = days === 1 ? "day ♡" : "days ♡";
    setCount(numWorkdays, fmt(reached ? 0 : countWorkdays(new Date(now), new Date(target))));
    setCount(numHours, fmt(Math.floor(remaining / 3600000)));
    numSeconds.textContent = fmt(Math.floor(remaining / 1000));
    pulse(numSeconds, reducedMotion, now);

    if (daysText.length !== lastDaysLen) {
      lastDaysLen = daysText.length;
      fitHero();
      fitter.refit(false);
    }

    const dayKey = localDayKey(new Date(now));
    if (reached !== wasReached || dayKey !== lastDayKey) {
      wasReached = reached;
      lastDayKey = dayKey;
      applyCopy(store.get().profanityLevel);
    }

    const span = Math.max(1, target - start);
    const pct = Math.min(100, Math.max(0, (Math.min(Math.max(now - start, 0), span) / span) * 100));
    pctLabel.textContent = `${Math.round(pct)}% done`;
    timeLeft.update(now, target);
    trackFill.style.width = `${pct.toFixed(1)}%`;
  }

  shareBtn.addEventListener("click", async () => {
    if (shareBtn.classList.contains("busy")) return;
    shareBtn.classList.add("busy");
    shareBtn.classList.remove("share-ok", "share-fail");
    const result = await shareCountdownCard({
      eventName: event!.name || "The reckoning",
      eventDate: formatEventDate(event!.targetDate),
      daysLeft: Math.floor(Math.max(0, new Date(event!.targetDate).getTime() - Date.now()) / 86400000),
      punchlineMain: punchlineMain.textContent ?? "",
      punchlineSimile: punchlineSub.textContent ?? "",
    });
    shareBtn.classList.remove("busy");
    shareBtn.classList.add(result === "failed" ? "share-fail" : "share-ok");
    window.setTimeout(() => shareBtn.classList.remove("share-ok", "share-fail"), 1600);
  });

  applyCopy(store.get().profanityLevel);
  tick();
  fitter.refit();

  // Entrance: glass floats up out of a blur while the numbers count up; the
  // first open after onboarding ends with the hero wobbling and a fizz.
  const firstRun = takeFirstRun();
  {
    const q = (sel: string) => root.querySelector<HTMLElement>(sel);
    const chips = root.querySelectorAll<HTMLElement>(".chr-chip");
    playEntrance(
      [q(".chr-topbar"), q(".chr-ticket"), headline, q(".chr-sub"), hero, chips[0], chips[1], q(".chr-seconds"), punchline, q(".chr-bottom")],
      "float",
      firstRun
    );
    const target = new Date(event.targetDate).getTime();
    const remaining = Math.max(0, target - Date.now());
    const ms = firstRun ? 1500 : 650;
    countUp(numDays, Math.floor(remaining / 86400000), fmt, ms, fitHero);
    countUp(numWorkdays, countWorkdays(new Date(), new Date(target)), fmt, ms);
    countUp(numHours, Math.floor(remaining / 3600000), fmt, ms);
    if (firstRun && !reducedMotion) {
      window.setTimeout(() => {
        if (!hero.isConnected) return;
        fizz(hero, 18, 2);
        hero.animate(
          [{ scale: "1 1" }, { scale: "1.06 .94", offset: 0.2 }, { scale: ".97 1.03", offset: 0.45 }, { scale: "1.01 .99", offset: 0.7 }, { scale: "1 1" }],
          { duration: 700, easing: "ease-out" }
        );
      }, 1500);
    }
  }
  const stopClock = everySecond(tick);
  const unmountBell = mountBell(root);
  const unsubscribe = store.subscribe((state) => {
    applyCopy(state.profanityLevel);
    fitter.refit(false);
  });

  return () => {
    stopClock();
    unmountBell();
    unsubscribe();
    dial.destroy();
    fitter.destroy();
  };
}
